import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { afterburnerApi } from '../../../../../api/patient/AftrburnerApi';
import Button from '../../../../../common/general/Button';
import ButtonGroup from '../../../../../common/general/ButtonGroup';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { SOMETHING_WENT_WRONG } from '../../../../../constants/notificationMessages';
import {
  ROLE_PRINCIPAL_INVESTIGATOR,
  ROLE_STUDY_MANAGER,
  ROLE_SYSTEM_ADMINISTRATOR
} from '../../../../../constants/userRoles';
import { userHasRole } from '../../../../../services/auth';
import { isAdverseEventLog, isReviewableLogType } from '../../../../../services/itemGroupQuestionService';
import { withParams } from '../../../../root/router/legacyComponentCompatability';
import WithAutoDirection from '../NewEncounterPage/WithAutoDirection/WithAutoDirection';
import { TABLE } from '../Review/reviewConstants';
import { generateResearchConnectStatus } from '../Review/ReviewContent/reviewContentService';
import { AE_NOT_REPORTED } from '../Worklist/constants';

import { EditorAndDateSelect } from './EditorAndDateSelect';

import './EncounterElementHeader.scss';

class EncounterElementHeader extends Component {
  static propTypes = {
    formData: PropTypes.any,
    whenWasDoneDate: PropTypes.string,
    whenWasDoneDateOnChange: PropTypes.func,
    whoDidItList: PropTypes.array,
    whoDidItOnChange: PropTypes.func,
    whoDidItUniqueIdentifier: PropTypes.string,
    isAbleToSave: PropTypes.func,
    isAbleToSign: PropTypes.func,
    onSaveData: PropTypes.func,
    afterburnerEnabled: PropTypes.bool,
    isAnyQuestionAnswerChanged: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      saveIconDisabled: false
    };
  }

  render() {
    const { props } = this,
      {
        formData,
        whenWasDoneDate,
        whenWasDoneDateOnChange,
        whoDidItList,
        whoDidItOnChange,
        whoDidItUniqueIdentifier,
        isAbleToSave,
        isAbleToSign,
        onSaveData,
        onInvestigatorSign,
        onPushToInvestigator,
        onSmConfirm,
        logRowStatus,
        checkedInEncountersForPatient,
        encounterOnChange,
        selectedEncounterId,
        showEncounterSelect,
        elementIcon,
        isDetailRowOpen,
        validationError,
        afterburnerEnabled,
        params,
        isAnyQuestionAnswerChanged,
        noSkippedQuestions
      } = props;

    const isEncounterElementHeaderVisible = () => {
      const isCommentLogs = formData && formData.hasOwnProperty('comments');
      const isTable = elementIcon === TABLE;

      if (isCommentLogs || isTable) {
        return !!isDetailRowOpen;
      }
      return true;
    };

    const isPushToInvestigatorShown = () => {
      return !!!logRowStatus?.piStatus && logRowStatus?.smStatus;
    };

    const isMarkToCompleteShown = () => {
      return logRowStatus?.smStatus && logRowStatus.smStatus === 'SM_REVIEW';
    };

    const isSignButtonNotShown = () => {
      return (
        !isAnyQuestionAnswerChanged() &&
        ((logRowStatus?.smStatus === 'SM_REVIEW' && logRowStatus?.piStatus === 'COMPLETE') ||
          (logRowStatus?.smStatus === 'COMPLETE' && logRowStatus?.piStatus === 'COMPLETE'))
      );
    };

    const showSaveButtonForSmAndSysAdmin = () => {
      return !!!logRowStatus?.piStatus || (isAnyQuestionAnswerChanged() && logRowStatus?.piStatus);
    };

    const showSaveButtonShownForPi = () => {
      return (
        !!!logRowStatus?.piStatus ||
        logRowStatus?.piStatus === 'DATA_REQUIRED' ||
        (logRowStatus?.piStatus && isAnyQuestionAnswerChanged() && !['DATA_REQUIRED'].includes(logRowStatus.piStatus))
      );
    };

    const isLogCanceled = () => {
      return logRowStatus && logRowStatus?.smStatus === 'CANCELED' && logRowStatus?.piStatus === 'CANCELED';
    };

    const isLogNotReported = () => {
      return logRowStatus?.piStatus === AE_NOT_REPORTED && logRowStatus?.smStatus === AE_NOT_REPORTED;
    };

    const signButtonIsVisible = () => {
      if (
        !isReviewableLogType(formData?.domainCode, formData?.elementIcon) ||
        isSignButtonNotShown() ||
        isLogNotReported()
      ) {
        return false;
      }

      if (userHasRole(ROLE_SYSTEM_ADMINISTRATOR)) {
        return true;
      }

      if (userHasRole(ROLE_STUDY_MANAGER)) {
        return false;
      }

      return userHasRole(ROLE_PRINCIPAL_INVESTIGATOR);
    };

    const saveButtonIsDisabled = () => {
      if (isReviewableLogType(formData?.domainCode, formData?.elementIcon)) {
        if (userHasRole(ROLE_SYSTEM_ADMINISTRATOR) || userHasRole(ROLE_STUDY_MANAGER)) {
          if (!showSaveButtonForSmAndSysAdmin()) {
            return true;
          }
        }

        if (userHasRole(ROLE_PRINCIPAL_INVESTIGATOR)) {
          if (!showSaveButtonShownForPi()) {
            return true;
          }
        }
      }
      return this.state.saveIconDisabled || isLogCanceled();
    };

    const markCompleteButtonIsDisabled = () => {
      if (!userHasRole(ROLE_SYSTEM_ADMINISTRATOR) && !userHasRole(ROLE_STUDY_MANAGER)) {
        return !isAbleToSign();
      }
      return isAnyQuestionAnswerChanged() || isLogCanceled();
    };

    const sendToResearchConnectButtonIsVisible =
      afterburnerEnabled && formData.elementIcon !== 'Table' && params?.patientEncounterId && !isLogNotReported();

    const saveOrSubmitButtonIsVisible = isAbleToSave() && !isLogNotReported();
    const saveOrSubmitAndAddNewButtonIsVisible = saveOrSubmitButtonIsVisible && params?.rowId === 'newRow';

    const pushToInvestigatorButtonIsVisible =
      isReviewableLogType(formData?.domainCode, formData?.elementIcon) &&
      userHasRole([ROLE_SYSTEM_ADMINISTRATOR, ROLE_STUDY_MANAGER]) &&
      isPushToInvestigatorShown();

    const markCompleteButtonIsVisible =
      isAdverseEventLog(formData?.domainCode, formData?.elementIcon) &&
      userHasRole([ROLE_SYSTEM_ADMINISTRATOR, ROLE_STUDY_MANAGER]) &&
      isMarkToCompleteShown();

    return (
      <WithAutoDirection>
        <div
          className={cx(
            'encounter-element-header py-3',
            isReviewableLogType(formData?.domainCode, formData?.elementIcon) ? 'adverse' : ''
          )}
        >
          {isEncounterElementHeaderVisible() && (
            <EditorAndDateSelect
              showEncounterSelect={showEncounterSelect}
              encounterOnChange={encounterOnChange}
              selectedEncounterId={selectedEncounterId}
              checkedInEncountersForPatient={checkedInEncountersForPatient}
              whenWasDoneDate={whenWasDoneDate}
              whenWasDoneDateOnChange={whenWasDoneDateOnChange}
              whoDidItList={whoDidItList}
              whoDidItUniqueIdentifier={whoDidItUniqueIdentifier}
              whoDidItOnChange={whoDidItOnChange}
              isAllowedToManage={isAbleToSave}
              validationError={validationError}
            />
          )}
          <ButtonGroup>
            {sendToResearchConnectButtonIsVisible && (
              <Button
                size="h28"
                onClick={() => sendToResearchConnect(params.ssuPatientId, params.patientItemGroupId)}
                disabled={isLogCanceled()}
              >
                Send to Research Connect
              </Button>
            )}
            <div className="item-group-buttons">
              {saveOrSubmitButtonIsVisible && (
                <Button
                  size="h28"
                  onClick={() => {
                    this.setState({ saveIconDisabled: true }, () => {
                      onSaveData().then(() => this.setState({ saveIconDisabled: false }));
                    });
                  }}
                  disabled={saveButtonIsDisabled()}
                >
                  {isReviewableLogType(formData?.domainCode, formData?.elementIcon) ? 'Save' : 'Submit'}
                </Button>
              )}
              {saveOrSubmitAndAddNewButtonIsVisible && (
                <Button
                  size="h28"
                  onClick={() => {
                    this.setState({ saveIconDisabled: true }, () => {
                      onSaveData(true).then(() => {
                        this.setState({ saveIconDisabled: false });
                      });
                    });
                  }}
                  disabled={saveButtonIsDisabled()}
                >
                  {isReviewableLogType(formData?.domainCode, formData?.elementIcon)
                    ? 'Save & Add New'
                    : 'Submit & Add New'}
                </Button>
              )}
              {pushToInvestigatorButtonIsVisible && (
                <Button
                  size="h28"
                  disabled={isLogCanceled()}
                  onClick={() => {
                    onPushToInvestigator();
                  }}
                >
                  Push To Investigator
                </Button>
              )}
              {signButtonIsVisible() && (
                <Button
                  size={'h28'}
                  disabled={isAnyQuestionAnswerChanged() || !noSkippedQuestions || isLogCanceled()}
                  onClick={() => {
                    onInvestigatorSign();
                  }}
                >
                  Sign
                </Button>
              )}
              {markCompleteButtonIsVisible && (
                <Button
                  size="h28"
                  disabled={markCompleteButtonIsDisabled()}
                  onClick={() => {
                    onSmConfirm();
                  }}
                >
                  Mark Complete
                </Button>
              )}
            </div>
          </ButtonGroup>
        </div>
      </WithAutoDirection>
    );
  }
}

const sendToResearchConnect = (ssuPatientId, sitePatientItemGroupId) => {
  afterburnerApi
    .sendToAfterburnerFromItemGroupPage(ssuPatientId, sitePatientItemGroupId)
    .then(({ data }) => {
      const afterburnerStatus = generateResearchConnectStatus(data);
      if (afterburnerStatus) {
        NotificationManager.info(afterburnerStatus);
      }
    })
    .catch(() => {
      NotificationManager.error(SOMETHING_WENT_WRONG);
    });
};

const mapStateToProps = state => {
  return {
    afterburnerEnabled: state.appInfo.features.afterburnerEnabled
  };
};

export const EncounterElementHeaderWithRouter = withParams(EncounterElementHeader);
export default connect(mapStateToProps, null)(EncounterElementHeaderWithRouter);
